import { useCallback, FC } from 'react';
import {
  ACCESS_INFO_SECTIONS,
  ACCESS_INFO_ACTIONS,
  StatusFilter,
  useNewProjectUrl,
  useProjectDataContext,
  useProjectSecurityContext,
} from 'domain/shared/newProject';
import { useParams } from 'react-router-dom';
import { UiThemeOverrider } from 'framework/uiTheme';
import { DropDownFilterInputLayout, ListFiltersLayout } from 'framework/list';
import OutcomesSensitiveAccordion from 'domain/projects/newProject/businessOutcomes/OutcomesSensitiveAccordion';
import { PreloadedSensitiveListLayout } from 'framework/sensitiveData';
import { openPageInNewTab } from 'framework/utils';
import ToggleSensitiveDataIconButton from 'domain/projects/newProject/finance/changeRequests/List/ToggleSensitiveDataIconButton';
import BillingListFactory from './BillingListFactory';
import IconButton from '@mui/material/IconButton';
import { useShowContext } from 'framework/show';
import { BILLING_STATUS_OPTIONS } from '../billingTypes';
import { EditFormShowButton } from 'framework/form';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { Tooltip } from '@mui/material';

const BillingList: FC = () => {
  const { id } = useParams<{ id: string }>();
  const { getSingleEntityUrl, getCreateBillingScheduleUrl, getUpdateBillingInvoiceDatesUrl } = useNewProjectUrl();
  const { data } = useShowContext();
  const { isAccessGranted } = useProjectSecurityContext();
  const { data: projectData } = useProjectDataContext();
  const hasSowId = !!projectData?.accountData?.sowId;

  const { hasBillingSchedule, billingScheduleId, error } = data || {};

  const filters = (
    <UiThemeOverrider
      overriddenLayouts={{
        DropdownLayout: DropDownFilterInputLayout,
      }}
    >
      <ListFiltersLayout>
        <StatusFilter source="status" options={BILLING_STATUS_OPTIONS} />
      </ListFiltersLayout>
    </UiThemeOverrider>
  );

  const renderActions = useCallback(() => {
    const hasManageAccess = isAccessGranted(ACCESS_INFO_SECTIONS.BILLING_SCHEDULE, ACCESS_INFO_ACTIONS.MANAGE);
    if (!id || error || !hasManageAccess) {
      return [];
    }

    const actions = [];

    if (!hasBillingSchedule) {
      actions.push(
        <IconButton
          sx={{ p: 0, ml: 1 }}
          key="add"
          onClick={() => hasSowId && openPageInNewTab(getCreateBillingScheduleUrl(id))}
        >
          <EditFormShowButton />
        </IconButton>,
      );
    }

    if (hasBillingSchedule && billingScheduleId) {
      actions.push(
        <Tooltip key="Edit billing schedule" title="Edit billing schedule">
          <IconButton
            sx={{ p: 0, ml: 1 }}
            key="Edit billing schedule button"
            onClick={() => openPageInNewTab(getCreateBillingScheduleUrl(id))}
          >
            <EditFormShowButton />
          </IconButton>
        </Tooltip>,
      );

      actions.push(
        <Tooltip key="Edit invoice dates" title="Edit invoice dates">
          <IconButton
            sx={{ p: 0, ml: 1 }}
            key="Edit invoice dates button"
            onClick={() => openPageInNewTab(getUpdateBillingInvoiceDatesUrl(billingScheduleId))}
          >
            <CalendarMonthOutlinedIcon color="primary" />
          </IconButton>
        </Tooltip>,
      );
    }

    return actions;
  }, [
    id,
    error,
    hasBillingSchedule,
    billingScheduleId,
    hasSowId,
    getCreateBillingScheduleUrl,
    getUpdateBillingInvoiceDatesUrl,
    isAccessGranted,
  ]);

  return (
    <PreloadedSensitiveListLayout
      timerId={getSingleEntityUrl()}
      toggleSensitiveDataComponent={ToggleSensitiveDataIconButton}
    >
      <OutcomesSensitiveAccordion defaultExpanded title="Billing schedule" filters={filters} actions={renderActions()}>
        <BillingListFactory />
      </OutcomesSensitiveAccordion>
    </PreloadedSensitiveListLayout>
  );
};

export default BillingList;
